/* eslint-disable max-classes-per-file */
import { Id } from 'types/Id'
import { CampaignConfig, CampaignDefaults, PPSOpportunitySolution } from './types'

function getBaseURL(): string {
  const baseURL = process.env.REACT_APP_PPS_API_BASE_URL
  // hotfix/create-opportunity-solution-campaign-Submission-1.1.3r297
  if (!baseURL) {
    console.error(`Base URL not found REACT_APP_PPS_API_BASE_URL: ${baseURL || 'undefined'}`)
    return ''
  }

  return baseURL
}

function getBaseHeaders(): Record<string, string> {
  const token = process.env.REACT_APP_PPS_TOKEN
  if (!token) {
    console.error(`Token not found REACT_APP_PPS_TOKEN: ${token || 'undefined'}`)
    return {}
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

class NotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NotFoundError'
  }
}

async function ensureOk(response: Response): Promise<unknown> {
  if (!response.ok) {
    throw new Error('Failed to fetch data')
  }

  return response.json()
}

class PPSServer {
  /**
   * Fetch an opportunity solution by ID
   */
  public static getOpportunitiesSolutionById(id: Id): Promise<PPSOpportunitySolution> {
    if (!id) throw new Error('Invalid ID')

    const url = `${getBaseURL()}/opportunity-solutions/${id}`
    const request = fetch(url, {
      method: 'GET',
      headers: getBaseHeaders(),
      credentials: 'include',
    })
    return request.then(ensureOk) as Promise<PPSOpportunitySolution>
  }

  /**
   * Fetch application default limits for campaign configuration
   */
  public static getOpportunitySolutionDefaults(): Promise<CampaignDefaults> {
    const url = `${getBaseURL()}/opportunity-solutions/defaults`
    const request = fetch(url, {
      method: 'GET',
      headers: getBaseHeaders(),
      credentials: 'include',
    })
    return request.then(ensureOk) as Promise<CampaignDefaults>
  }

  /**
   * Submit a new opportunity solution with optional campaign configuration
   */
  public static submitOpportunitySolution(
    opportunityId: Id,
    config?: CampaignConfig,
  ): Promise<PPSOpportunitySolution> {
    const url = `${getBaseURL()}/opportunity-solutions`
    const request = fetch(url, {
      method: 'POST',
      headers: getBaseHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        opportunityId,
        ...(config && {
          targetEmailCount: config.targetEmailCount,
          maxRetry: config.maxRetry,
          aiConfig: config.aiConfig,
          testMode: config.testMode,
          cherreLocationLimit: config.cherreLocationLimit,
          maxApolloOrgs: config.maxApolloOrgs,
        }),
      }),
    })
    return request.then(ensureOk) as Promise<PPSOpportunitySolution>
  }

  /**
   * Download the CSV of contacted contacts for an opportunity solution
   * Returns a Blob which can be used to trigger a file download in the UI
   */
  public static async downloadOpportunitySolutionContactedCsv(id: Id): Promise<Blob> {
    if (!id) throw new Error('Invalid ID')

    const url = `${getBaseURL()}/opportunity-solutions/${id}/contacted_csv`
    const response = await fetch(url, {
      method: 'GET',
      headers: getBaseHeaders(),
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new NotFoundError('Opportunity solution CSV not found')
      }
      throw new Error(`Failed to download CSV: ${response.statusText}`)
    }

    return response.blob()
  }

  /**
   * Trigger a re-run of an opportunity solution with configuration
   */
  public static rerunOpportunitySolution(
    id: Id,
    config: {
      targetEmailCount?: number
      maxRetry: number
      aiConfig: { jobTitleAgent: boolean; apolloOrgSearchAgent: boolean }
      testMode?: boolean
      cherreLocationLimit?: number
      maxApolloOrgs?: number
    },
  ): Promise<PPSOpportunitySolution> {
    if (!id) throw new Error('Invalid ID')

    const url = `${getBaseURL()}/opportunity-solutions/${id}/rerun`
    const request = fetch(url, {
      method: 'POST',
      headers: getBaseHeaders(),
      credentials: 'include',
      body: JSON.stringify(config),
    })
    return request.then(ensureOk) as Promise<PPSOpportunitySolution>
  }
}

export default { PPSServer, NotFoundError }
export { PPSServer, NotFoundError }
